/**
 * LUNA Coding Tutor - Flow Engine Hook
 * 원본: flow.js, lesson.js의 핵심 흐름 제어 로직을 Custom Hook으로 재구현
 */

import { useCallback, useRef } from 'react';
import { useLunaStore, APP_STATES, CHAPTER_STAGES, LEARNING_TYPES } from '@/store/useLunaStore';
import api from '@/services/api';

// ============================================
// 상수 및 유틸리티
// ============================================

const CAREER_GOAL_PATTERNS = [
  /되고\s*싶어|되려면|개발자|엔지니어|디자이너|기획자|분석가/i,
  /로드맵|커리어|경력|취업|전환|career|roadmap/i,
  /장기\s*계획|장기\s*학습|마스터|전문가/i,
  /풀스택|프론트엔드|백엔드|데이터|AI|머신러닝|클라우드/i,
];

const POSITIVE_KEYWORDS = ['y', 'yes', '네', '넵', '예', '좋아', '좋아요', '진행', '시작', 'go', 'ok', '오케이'];
const NEXT_STEP_PATTERN = /(다음\s*단계|다음\s*문제|next|next\s*step|next\s*problem)/i;

export function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function isPositiveResponse(text) {
  const t = (text || '').trim().toLowerCase();
  return POSITIVE_KEYWORDS.some((k) => t.includes(k));
}

export function isCareerGoalRequest(text) {
  const t = (text || '').trim();
  let matchCount = 0;
  for (const pattern of CAREER_GOAL_PATTERNS) {
    if (pattern.test(t)) matchCount++;
  }
  return matchCount >= 1 && t.length > 10;
}

function collectPreviousLessons(curriculum, currentLessonTitle) {
  const previousLessons = [];

  if (!curriculum || !Array.isArray(curriculum.curriculum)) {
    return previousLessons;
  }

  for (const step of curriculum.curriculum) {
    if (step.title === currentLessonTitle) {
      break;
    }

    if (step.completed && step.learned_keywords?.length > 0) {
      previousLessons.push({
        title: step.title,
        learned_keywords: step.learned_keywords,
      });
    }
  }

  return previousLessons;
}

// ============================================
// 메인 Hook
// ============================================

export function useLunaFlow() {
  const store = useLunaStore();
  const {
    appState,
    currentCurriculum,
    currentStepIndex,
    currentLessonPlan,
    currentLessonStepIndex,
    chatHistory,
    intendedTopic,
    isRoadmapMode,
    roadmapContext,
    pendingRegenerateType,
    lessonCompleted,
    chatInputLocked,
    problemJSON,

    // 액션
    setAppState,
    setCurriculum,
    setStepIndex,
    setLessonPlan,
    addChatMessage,
    setChatInputLocked,
    setPendingNextStepOffer,
    setPendingRegenerateType,
    setIntendedTopic,
    setLessonCompleted,
    setLoading,
    setTutorExpression,
    goToNextLessonStep,
    goToPrevLessonStep,
    goToNextCurriculumStep,
    resetAll: storeResetApp,
  } = store;

  const abortControllerRef = useRef(null);

  // ============================================
  // 앱 초기화 (Reset)
  // ============================================

  const resetApp = useCallback((options = {}) => {
    console.log('[Flow] 앱 초기화 (Reset)', options);

    // [Legacy Support] boolean이 넘어오면 객체로 변환
    const resetOptions = typeof options === 'boolean' ? {} : options;

    // 초기 환영 메시지 생성
    const initialMessage = {
      role: 'ai',
      content: '안녕하세요! 새로운 학습을 시작해볼까요? 배우고 싶은 주제가 있다면 말씀해주세요. 😊\n(예: "React 기초", "파이썬으로 데이터 분석하기", "풀스택 개발자 되기")',
      timestamp: new Date().toISOString()
    };

    // [Fix] 초기화와 동시에 메시지 설정 (Race Condition 방지)
    // [Refactor] useLunaStore.getState() 사용
    const state = useLunaStore.getState();
    state.resetAll([initialMessage], resetOptions);

    // 추가적인 상태 보정
    state.setLoading(false);
    state.setChatInputLocked(false);

    // [Safety] 렌더링 사이클 문제로 잠금이 풀리지 않는 경우를 대비한 안전장치
    setTimeout(() => {
      useLunaStore.getState().setChatInputLocked(false);
    }, 100);

  }, []); // 의존성 제거 (getState 사용하므로 안전)

  // ============================================
  // 로드맵/커리큘럼 생성
  // ============================================

  const generateRoadmap = useCallback(async (goal, type = 'career') => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();

    try {
      const loadingMsg = type === 'course'
        ? '🎓 AI가 과목 마스터 코스를 설계하고 있어요...'
        : '🗺️ AI가 맞춤 커리어 로드맵을 설계하고 있어요...';
      state.setLoading(true, loadingMsg);

      // [신규] roadmapType 전달 ('course' or 'career')
      const roadmap = await api.generateRoadmap(goal, type);

      // [Refactor] await 이후 상태 재조회
      const currentState = useLunaStore.getState();

      currentState.setTutorExpression('happy');
      const completionMsg = type === 'course'
        ? `✨ **"${escapeHtml(currentState.intendedTopic || goal)}"** 마스터 코스를 완성했어요!\n\n단기간에 핵심만 쏙쏙 배워봐요! 🔥`
        : `✨ 멋져요! **"${escapeHtml(currentState.intendedTopic || goal)}"**를 위한 커리어 로드맵을 완성했어요!\n\n카드의 버튼을 눌러 진행해주세요~`;

      // [Fix] 완료 메시지와 로드맵 카드를 순서대로 추가
      currentState.addChatMessage({
        role: 'ai',
        content: completionMsg,
        skipTyping: true
      });

      currentState.addChatMessage({
        role: 'ai',
        type: 'roadmap_card',
        content: { ...roadmap, type }
      });

      currentState.setCurrentRoadmap({ ...roadmap, type });

      // [Fix] 로드맵 모드 활성화 및 기존 커리큘럼 상태 정리
      currentState.setIsRoadmapMode(true);
      currentState.setCurriculum(null); // 기존 커리큘럼 제거 (완전 교체)

      currentState.setChatInputLocked(true, '카드 버튼을 눌러주세요');
      currentState.setAppState(APP_STATES.ROADMAP_GENERATED);

      return { success: true, roadmap };
    } catch (error) {
      console.error('[Flow] 로드맵 생성 실패:', error);
      const errorState = useLunaStore.getState();

      errorState.setChatInputLocked(false);

      const errorMsg = error.message || '알 수 없는 오류';
      errorState.addChatMessage({
        role: 'ai',
        content: `로드맵 생성 중 문제가 발생했어요 😢\n\n**${errorMsg}**`,
        type: 'error_retry',
        retryContext: { type: 'roadmap', goal, roadmap_type: type }
      });

      errorState.setAppState(APP_STATES.IDLE);
      return { success: false, error: errorMsg };
    } finally {
      useLunaStore.getState().setLoading(false);
    }
  }, []); // 의존성 제거

  const generateCurriculum = useCallback(async (topic) => {
    try {
      setLoading(true, '커리큘럼을 생성하고 있어요...');

      // [Fix] store 클로저 대신 최신 상태를 직접 읽음 (stale closure 방지)
      const latestState = useLunaStore.getState();
      const isFromRoadmap = latestState.needsCurriculumGeneration;

      // 즉시 플래그를 해제하여 다른 이벤트를 통한 중복 실행 방지
      if (isFromRoadmap) {
        latestState.setNeedsCurriculumGeneration(false);
      }

      let context = null;
      if (latestState.isRoadmapMode && latestState.roadmapContext) {
        context = {
          roadmap_title: latestState.roadmapContext.roadmapTitle,
          career_goal: latestState.roadmapContext.career_goal || '',
          phase_index: latestState.roadmapContext.pIdx,
          topic_index: latestState.roadmapContext.tIdx,
          ...latestState.roadmapContext
        };
        console.log('[Flow] 로드맵 컨텍스트로 커리큘럼 생성:', {
          topic,
          pIdx: latestState.roadmapContext.pIdx,
          tIdx: latestState.roadmapContext.tIdx,
          roadmapTitle: latestState.roadmapContext.roadmapTitle
        });
      }

      const resp = await api.generateCurriculum(topic, context);

      if (resp?.is_broad) {
        const question = resp.clarification_question || '어떤 세부 분야에 관심이 있으신가요?';
        addChatMessage('ai', question);

        setAppState(APP_STATES.AWAITING_TOPIC_REFINEMENT);
        return {
          success: true,
          isBroad: true,
          suggestedTopics: resp.suggested_topics || [],
          clarificationQuestion: question,
        };
      }

      setCurriculum(resp);
      setStepIndex(0);
      const currentState = useLunaStore.getState();
      currentState.setCurriculumProgress({});
      setTutorExpression('teaching');

      // [Fix] 커리큘럼 카드를 채팅 내역에 추가 (자연스러운 흐름)
      currentState.addChatMessage({
        role: 'ai',
        type: 'curriculum_card',
        content: resp
      });

      // [Fix] isFromRoadmap 지역 변수를 사용하여 원자적 체크 수행
      // 다른 이벤트를 통한 중복 호출 시에도 최초 설정값을 유지함
      if (currentState.isRoadmapMode && !isFromRoadmap) {
        currentState.setIsRoadmapMode(false);
        currentState.setCurrentRoadmap(null);
        addChatMessage('system', '새로운 커리큘럼을 시작합니다. (기존 로드맵은 초기화되었습니다)');
      }

      setChatInputLocked(true, '학습할 단계를 클릭해주세요');
      setAppState(APP_STATES.CURRICULUM_GENERATED);

      // [Fix] 상태 변경 후 즉시 DB 저장 (새로고침/이동 시 유지되도록)
      // 상태 업데이트가 비동기 반영될 수 있으므로 약간의 지연 후 저장하거나, 곧바로 호출
      setTimeout(() => saveCurrentState(), 100);

      return {
        success: true,
        isBroad: false,
        curriculum: resp,
      };
    } catch (error) {
      console.error('[Flow] 커리큘럼 생성 실패:', error);
      setChatInputLocked(false);

      const errorMsg = error.message || '알 수 없는 오류';
      // [Fix] 재시도 버튼을 위한 에러 메시지 타입 전송
      addChatMessage({
        role: 'ai',
        content: `커리큘럼 생성 중 문제가 발생했어요 😢\n\n**${errorMsg}**`,
        type: 'error_retry',
        retryContext: { type: 'curriculum', topic }
      });

      setAppState(APP_STATES.IDLE);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      // [Safety] 에러 발생 시에도 플래그 정리 보장
      if (useLunaStore.getState().needsCurriculumGeneration) {
        useLunaStore.getState().setNeedsCurriculumGeneration(false);
      }
    }
  }, [setLoading, addChatMessage, setAppState, setCurriculum, setTutorExpression, setChatInputLocked]);

  // ============================================
  // 레슨 시작/진행
  // ============================================

  // ============================================
  // 레슨 시작/진행
  // ============================================

  const startLesson = useCallback(async (topic, lessonTitle, stepIndexOverride = null) => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();
    const { currentCurriculum, currentStepIndex, isRoadmapMode, roadmapContext } = state;

    // [Fix] stepIndexOverride가 있으면 그 값 사용, 없으면 store의 currentStepIndex 사용
    // handleStepClick에서 setStepIndex 후 바로 호출하면 closure 문제로 이전 값이 사용되기 때문
    const effectiveStepIndex = stepIndexOverride !== null ? stepIndexOverride : currentStepIndex;

    console.log('[LESSON] startLesson 시작 - topic:', topic, 'lessonTitle:', lessonTitle, 'effectiveStepIndex:', effectiveStepIndex);

    state.setChatInputLocked(false);
    state.setLoading(true, '수업 계획을 생성 중입니다...');

    try {
      const previousLessons = collectPreviousLessons(currentCurriculum, lessonTitle);

      let roadmapCtx = null;
      if (isRoadmapMode && roadmapContext) {
        roadmapCtx = {
          phase_index: roadmapContext.pIdx ?? 0,
          topic_index: roadmapContext.tIdx ?? 0,
          step_index: effectiveStepIndex ?? 0,
        };
      }

      const plan = await api.generateLessonPlan({
        topic,
        lessonTitle,
        previousLessons,
        roadmapContext: roadmapCtx,
        currentStepIndex: effectiveStepIndex, // [Fix] 명시적으로 전달
      });

      const steps = Array.isArray(plan?.steps) ? plan.steps : [];
      if (plan?.error || steps.length === 0) {
        return {
          success: false,
          error: plan?.error_message || '수업 계획 생성에 실패했습니다.',
        };
      }

      // [Refactor] await 이후 상태 재조회
      const nextState = useLunaStore.getState();

      nextState.setLessonPlan(plan);
      nextState.setLessonStepIndex(0);
      nextState.setLessonCompleted(false);
      nextState.setAppState(APP_STATES.CONCEPT_VIEW); // [Fix] 레슨 시작 시 뷰 상태 강제 전환

      // 채팅 히스토리 초기화 - 새 단원 시작 시 이전 대화 제거
      nextState.setChatHistory([]);

      // [Fix] 레슨 시작 메시지 추가 (히스토리 초기화 직후)
      nextState.addChatMessage('ai', `📖 **"${lessonTitle}"** 레슨을 시작합니다! 화면의 내용을 따라가 보세요. 💪`);

      await saveCurrentState();

      return { success: true, plan };
    } catch (error) {
      console.error('[LESSON] startLesson 에러:', error);
      const errorState = useLunaStore.getState();

      // [Fix] 에러 발생 시 사용자에게 알림
      errorState.addChatMessage('ai', '레슨을 생성하는 과정에서 일시적인 문제가 발생했어요. 잠시 후 다시 시도해 주시겠어요? 😥');
      return {
        success: false,
        error: error.message || '알 수 없는 오류',
      };
    } finally {
      useLunaStore.getState().setLoading(false);
    }
  }, []); // 의존성 제거

  const handleStepClick = useCallback(async (stepIndex) => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();
    const { currentCurriculum, intendedTopic } = state;

    const steps = currentCurriculum?.curriculum || [];
    const step = steps[stepIndex];

    if (!step) return { success: false, error: '단계 정보를 찾을 수 없습니다.' };

    state.setStepIndex(stepIndex);
    state.setTutorExpression('teaching');

    const lessonTitle = step.title || '선택한 단계';
    state.addChatMessage({
      role: 'ai',
      content: `"${lessonTitle}" 단계를 선택하셨군요! 수업을 시작할게요.`,
      skipTyping: true
    });

    const topic = intendedTopic || currentCurriculum?.topic || '코딩';
    // [Fix] stepIndex를 명시적으로 전달하여 stale closure 문제 해결
    return await startLesson(topic, lessonTitle, stepIndex);
  }, [startLesson]); // startLesson은 이제 의존성 없음(빈 배열)이지만, 여기서 호출하므로 deps에 둬도 되고 빼도 됨. startLesson이 stable하다면 OK.

  const goToNextStep = useCallback(async () => {
    console.log('[Flow] goToNextStep triggered.');
    const state = useLunaStore.getState();

    state.setLoading(true, '다음 단계를 준비하고 있어요...');

    try {
      // 현재 완료된 레슨 데이터를 goToNextCurriculumStep이 상태를 초기화하기 전에 캡처
      const { currentCurriculum, currentStepIndex, intendedTopic, currentLessonPlan, lastCodeSnapshot } = state;
      const completedStep = currentCurriculum?.curriculum?.[currentStepIndex];
      const completedTopic = intendedTopic || currentCurriculum?.topic || '코딩';
      const completedTitle = completedStep?.title || currentLessonPlan?.title || '';
      if (completedTitle) {
        api.completeLesson({
          topic: completedTopic,
          lessonTitle: completedTitle,
          summary: completedTitle,
          userCode: lastCodeSnapshot || '',
          learnedKeywords: currentLessonPlan?.learned_keywords || completedStep?.learned_keywords || [],
        }).catch(e => console.warn('[Flow] completeLesson 저장 실패:', e));
      }

      // [Fix] nextIndex도 받아서 startLesson에 전달
      // [Refactor] Action도 getState()로 호출
      const { isComplete, nextStep, nextIndex } = state.goToNextCurriculumStep();

      if (isComplete) {
        // [Refactor] 완료 시점의 최신 상태 재조회
        const completionState = useLunaStore.getState();

        completionState.setTutorExpression('proud');

        if (completionState.isRoadmapMode && completionState.roadmapContext) {
          try {
            console.log('[Flow] Unlocking Next Topic with Context:', completionState.roadmapContext);
            const unlockResult = await api.unlockNextTopic(
              completionState.roadmapContext.pIdx,
              completionState.roadmapContext.tIdx,
              completionState.roadmapContext.topicId // [New] 토픽 ID 전달
            );

            // [Fix] 잠금 해제된 토픽 정보를 스토어에 저장 (RoadmapPage 애니메이션용)
            // [Refactor] await 직후라 안전하지만, 원칙상 getState() 사용
            if (unlockResult && unlockResult.unlocked_topic) {
              console.log('[Flow] 다음 토픽 잠금 해제:', unlockResult.unlocked_topic);
              useLunaStore.getState().setNewlyUnlockedTopic(unlockResult.unlocked_topic);
            }
          } catch (e) {
            console.warn('[Flow] unlock_next 호출 실패:', e);
          }

          // [Refactor] intendedTopic 최신값 사용
          const currentTopic = useLunaStore.getState().intendedTopic;
          useLunaStore.getState().addChatMessage('ai', `🎊 **${currentTopic}** 토픽을 모두 완료했어요! 🎉`);
          return { isComplete: true, shouldNavigateToRoadmap: true };
        }

        completionState.addChatMessage('ai', '🎊 와, 전체 커리큘럼을 완료하셨어요! 정말 대단해요!\n\n🗺️ 로드맵이 있다면 **[로드맵]** 버튼을, 새로운 주제로 시작하려면 **[새 주제]** 버튼을 눌러주세요.');
        completionState.setAppState(APP_STATES.IDLE);
        return { isComplete: true, shouldNavigateToRoadmap: false };
      }

      // [Refactor] 다음 단계 진행 시 최신 상태 참조
      const nextStepState = useLunaStore.getState();
      const topic = nextStepState.intendedTopic || nextStepState.currentCurriculum?.topic || '코딩';
      const lessonTitle = nextStep.title;

      // [Fix] nextIndex를 명시적으로 전달하여 stale closure 문제 해결
      const result = await startLesson(topic, lessonTitle, nextIndex);
      return { isComplete: false, ...result };
    } catch (error) {
      console.error('[Flow] 다음 단계 수업 시작 중 오류:', error);
      const errorState = useLunaStore.getState();
      errorState.setTutorExpression('encouraging');
      errorState.addChatMessage('ai', '앗, 다음 단계를 불러오는 중 문제가 생겼어요 😢 다시 시도해볼게요.');
      return { success: false, error: error.message };
    } finally {
      useLunaStore.getState().setLoading(false);
    }
  }, [startLesson]); // 의존성 최소화

  // ============================================
  // 채팅 처리
  // ============================================

  // ============================================
  // 채팅 처리
  // ============================================

  const handleInProblemChat = useCallback(async (question, onChunk, onComplete) => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();
    const {
      appState,
      currentLessonPlan,
      currentLessonStepIndex,
      problemJSON,
      chatHistory,
      currentCurriculum
    } = state;

    const q = String(question || '').trim().toLowerCase();

    if (appState !== APP_STATES.CHAPTER_COMPLETE && NEXT_STEP_PATTERN.test(q)) {
      return await goToNextStep();
    }

    try {
      // [Fix] 로딩 상태 명시적 활성화
      useLunaStore.getState().setLoading(true);

      let currentLessonStep = null;
      if (currentLessonPlan?.steps && currentLessonStepIndex >= 0) {
        const step = currentLessonPlan.steps[currentLessonStepIndex];
        if (step) {
          currentLessonStep = {
            index: currentLessonStepIndex,
            total: currentLessonPlan.steps.length,
            type: step.step_type || step.type,
            text: step.text || '',
            question: step.question || '',
            code_snippet: step.code_snippet || step.code_template || step.code_example || '',
            options: step.options || null,
          };
        }
      }

      const result = await api.chatStream(
        {
          problem: problemJSON || {},
          user_code: '',
          chat_history: chatHistory || [],
          question,
          curriculum: currentCurriculum || null,
          current_step: currentLessonPlan ? {
            title: currentLessonPlan.title,
            description: currentLessonPlan.description,
          } : null,
          current_lesson_step: currentLessonStep,
        },
        onChunk,
        (fullText) => {
          // [Fix] 스트리밍 완료 후 히스토리에 추가할 때 wasStreamed 플래그 부여
          // 및 로딩 상태 해제 (Race Condition 방지)
          const state = useLunaStore.getState();
          state.addChatMessage({
            role: 'ai',
            content: fullText,
            wasStreamed: true
          });
          state.setLoading(false);

          if (onComplete) onComplete(fullText);
        }
      );

      return { success: true, answer: result.answer };
    } catch (error) {
      console.error('[Chat] 채팅 오류:', error);
      const errorState = useLunaStore.getState();
      errorState.setLoading(false); // [Fix] 에러 시 로딩 해제 보장
      errorState.setTutorExpression('encouraging');
      errorState.addChatMessage('ai', '앗, 답변을 생성하는 중 문제가 생겼어요 😢 다시 질문해주세요.');
      return { success: false, error: error.message };
    }
  }, [goToNextStep]); // goToNextStep은 다른 의존성 없으므로(내부 refactoring됨) stable함

  const handleChatSubmit = useCallback(async (userInput, callbacks = {}) => {
    const { onChunk, onComplete, onLearningTypeRequired, onTopicOptions, onConsultation } = callbacks;

    if (!userInput?.trim()) return { success: false };

    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();

    if (state.chatInputLocked) {
      state.addChatMessage('ai', '위의 버튼을 선택해주세요! 💡');
      return { success: false, locked: true };
    }

    state.addChatMessage('user', userInput);
    state.setLoading(true); // Simplified: Just dots

    try {
      if (state.appState === APP_STATES.CHAPTER_COMPLETE || state.lessonCompleted) {
        return await handleInProblemChat(userInput, onChunk, onComplete);
      }

      if (state.pendingNextStepOffer) {
        if (isPositiveResponse(userInput) || NEXT_STEP_PATTERN.test(userInput)) {
          state.setPendingNextStepOffer(false);
          return await goToNextStep();
        } else {
          state.setPendingNextStepOffer(false);
        }
      }

      if (state.appState === APP_STATES.AWAITING_REGENERATE_INPUT) {
        const regenerateType = state.pendingRegenerateType;
        state.setPendingRegenerateType(null);
        state.setChatInputLocked(false);

        if (regenerateType === LEARNING_TYPES.ROADMAP) {
          const modifiedGoal = `${state.intendedTopic} (수정 요청: ${userInput})`;
          return await generateRoadmap(modifiedGoal);
        } else {
          const modifiedTopic = `${state.intendedTopic} (수정 요청: ${userInput})`;
          return await generateCurriculum(modifiedTopic);
        }
      }

      switch (state.appState) {
        case APP_STATES.IDLE: {
          state.setIntendedTopic(userInput);

          // [Mod] 로드맵 모드여도 무조건 상담을 먼저 수행 (Side Quest vs 교체 선택을 위해)
          // if (isRoadmapMode) {
          //   return await generateCurriculum(userInput);
          // }

          try {
            const consultData = await api.consultLearningPath(userInput);

            // [Refactor] await 이후 상태 재조회
            const nextState = useLunaStore.getState();

            nextState.setAppState(APP_STATES.AWAITING_PATH_SELECTION);
            nextState.setChatInputLocked(true, '학습 방향을 선택해주세요');
            nextState.setLoading(false); // [Fix] 상담 데이터 도착 후 로딩 해제

            if (onConsultation) {
              onConsultation(consultData);
            }
            return { success: true, consultation: consultData };
          } catch (e) {
            console.warn('[Flow] 상담 실패, fallback to type selector:', e);
            const fallbackState = useLunaStore.getState();

            fallbackState.setTutorExpression('teaching');
            fallbackState.addChatMessage({
              role: 'ai',
              content: `좋은 주제예요! ✨ **"${escapeHtml(userInput)}"**을(를) 어떻게 학습하고 싶으신가요?`,
              skipTyping: true
            });
            fallbackState.setChatInputLocked(true, '학습 유형을 선택해주세요');
            fallbackState.setAppState(APP_STATES.AWAITING_LEARNING_TYPE);
            fallbackState.setLoading(false); // [Fix] Fallback 진입 시 로딩 해제

            if (onLearningTypeRequired) {
              onLearningTypeRequired(userInput);
            }
            return { success: true, requiresLearningTypeSelection: true, topic: userInput };
          }
        }

        case APP_STATES.AWAITING_PATH_SELECTION:
          state.addChatMessage('ai', '위의 상담 카드에서 원하시는 방향을 선택해주세요! 💡\n\n다른 방향을 원하시면 **[다른 방향으로 추천받기]** 버튼을 눌러주세요.');
          return { success: false, locked: true };

        case APP_STATES.AWAITING_LEARNING_TYPE:
          state.addChatMessage('ai', '위의 버튼에서 학습 유형을 선택해주세요! 💡\n\n🗺️ **장기 로드맵**: 체계적인 커리어 성장 경로\n📚 **단기 커리큘럼**: 빠르게 핵심만 학습');
          return { success: false, locked: true };

        case APP_STATES.ROADMAP_GENERATED:
        case APP_STATES.CURRICULUM_GENERATED:
          state.addChatMessage('ai', '위의 버튼을 눌러 진행 방향을 선택해주세요! 💡\n\n✅ **이대로 진행**: 생성된 내용으로 학습 시작\n🔄 **다시 생성**: 다른 방향으로 새로 만들기');
          return { success: false, locked: true };

        case APP_STATES.AWAITING_TOPIC_REFINEMENT: {
          state.setIntendedTopic(userInput);
          const result = await generateCurriculum(userInput);

          if (result.isBroad && onTopicOptions) {
            onTopicOptions(result.suggestedTopics);
          }

          return result;
        }

        case APP_STATES.CURRICULUM_PROPOSED:
          if (isPositiveResponse(userInput)) {
            state.setStepIndex(0);
            const currentStep = state.currentCurriculum?.curriculum?.[0] || {};

            state.setTutorExpression('teaching');
            state.addChatMessage({
              role: 'ai',
              content: `좋습니다! 그럼 첫 번째 단계인 "${currentStep.title || '기초'}"에 대해 자세히 알아볼게요.`,
              skipTyping: true
            });

            const topic = state.intendedTopic || state.currentCurriculum?.topic || '코딩';
            return await startLesson(topic, currentStep.title || '핵심 개념');
          } else {
            state.addChatMessage('ai', '알겠습니다. 다른 주제로 다시 시작하려면 상단의 [새 주제]를 눌러주세요.');
            state.setLoading(false); // [Fix] 취소 메시지 표시 후 로딩 해제
            resetApp(false);
            return { success: false, cancelled: true };
          }

        case APP_STATES.CONCEPT_VIEW:
          return await handleInProblemChat(userInput, onChunk, onComplete);

        case APP_STATES.PROBLEM_SOLVING:
          return await handleInProblemChat(userInput, onChunk, onComplete);

        case APP_STATES.LESSON_EXPLAINED:
          if (isPositiveResponse(userInput)) {
            return { success: true, shouldStartProblem: true };
          } else {
            return await handleInProblemChat(userInput, onChunk, onComplete);
          }

        case APP_STATES.CURRICULUM_CHOICE:
        default: {
          const hasContext = state.currentLessonPlan || state.problemJSON || state.currentCurriculum;

          if (hasContext) {
            return await handleInProblemChat(userInput, onChunk, onComplete);
          } else {
            state.setAppState(APP_STATES.IDLE);
            state.setIntendedTopic(userInput);
            return await generateCurriculum(userInput);
          }
        }
      }
    } catch (error) {
      console.error('[Flow] 에러 발생:', error);
      const errorState = useLunaStore.getState();
      errorState.setTutorExpression('encouraging');

      const errorMsg = error.message || '알 수 없는 오류가 발생했어요.';
      errorState.addChatMessage('ai', `앗, 오류가 발생했어요 😢\n\n**${errorMsg}**\n\n잠시 후 다시 시도해주세요.`);
      errorState.setLoading(false); // [Fix] 에러 시 수동 해제

      return { success: false, error: errorMsg };
    }
  }, [
    generateRoadmap, generateCurriculum, startLesson, goToNextStep, handleInProblemChat, resetApp
  ]); // 의존성 최소화

  // ============================================
  // 상태 저장
  // ============================================

  const saveCurrentState = useCallback(async () => {
    const userId = localStorage.getItem('coding_tutor_user_id');
    if (!userId) return;

    const currentStore = useLunaStore.getState();

    // [Fix] 로그인 사용자만 저장 (게스트는 저장하지 않음)
    if (!currentStore.username) {
      console.log('[SAVE] 게스트 사용자 - 상태 저장 스킵');
      return;
    }

    // [Fix] 샘플 데모 커리큘럼은 저장하지 않음
    let curriculumToSave = currentStore.currentCurriculum;
    if (curriculumToSave?.topic === 'Python 기초: 변수와 데이터' &&
      curriculumToSave?.curriculum?.length === 3 &&
      curriculumToSave?.curriculum?.[0]?.title?.includes('데이터 담는 상자')) {
      console.log('[SAVE] 샘플 데모 커리큘럼 감지 - 저장하지 않음');
      curriculumToSave = null;
    }

    const safeState = {
      appState: currentStore.appState,
      currentChapterStage: currentStore.currentChapterStage || CHAPTER_STAGES.IDLE,
      intendedTopic: currentStore.intendedTopic,
      currentCurriculum: curriculumToSave,
      currentLessonPlan: currentStore.currentLessonPlan,
      currentStepIndex: currentStore.currentStepIndex,
      currentLessonStepIndex: currentStore.currentLessonStepIndex,
      chatHistory: currentStore.chatHistory.slice(-100),
      lastCodeSnapshot: currentStore.lastCodeSnapshot,
      lessonCompleted: currentStore.lessonCompleted,
      isRoadmapMode: currentStore.isRoadmapMode,
      roadmapContext: currentStore.roadmapContext,
      problemJSON: currentStore.problemJSON,
    };

    try {
      await api.saveState(userId, safeState);
      console.log('[SAVE] 상태 저장 완료 (Fresh State)');
    } catch (error) {
      console.warn('[SAVE] 상태 저장 실패:', error);
    }
  }, []);

  // ============================================
  // 학습 유형 선택 처리
  // ============================================

  // ============================================
  // 학습 유형 선택 처리
  // ============================================

  const selectRoadmapLearning = useCallback(async () => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();
    state.setChatInputLocked(false);
    return await generateRoadmap(state.intendedTopic);
  }, [generateRoadmap]);

  const selectCurriculumLearning = useCallback(async () => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();
    state.setChatInputLocked(false);
    return await generateCurriculum(state.intendedTopic);
  }, [generateCurriculum]);

  // ============================================
  // 레슨 단계 네비게이션
  // ============================================

  const goToNextLessonStepWithSave = useCallback(async () => {
    // [Refactor] Action도 최신 상태에서 호출 (일관성)
    const result = useLunaStore.getState().goToNextLessonStep();
    await saveCurrentState();
    return result;
  }, [saveCurrentState]);

  const goToPrevLessonStepWithSave = useCallback(async () => {
    // [Refactor] Action도 최신 상태에서 호출 (일관성)
    const moved = useLunaStore.getState().goToPrevLessonStep();
    if (moved) {
      await saveCurrentState();
    }
    return moved;
  }, [saveCurrentState]);

  // ============================================
  // 재생성 요청 처리
  // ============================================

  const requestRegenerate = useCallback((type) => {
    // [Refactor] 항상 최신 상태 참조
    const state = useLunaStore.getState();

    state.addChatMessage('ai', `어떤 방향으로 수정하면 좋을까요? 🤔\n\n예시:\n${type === LEARNING_TYPES.ROADMAP
      ? '- "백엔드보다는 프론트엔드 위주로"\n- "실무 프로젝트를 더 많이 넣어줘"\n- "6개월 안에 완료할 수 있게"'
      : '- "좀 더 기초부터 시작했으면 좋겠어"\n- "실습 위주로 구성해줘"\n- "단계를 좀 더 세분화해줘"'
      }`);
    state.setPendingRegenerateType(type);
    state.setAppState(APP_STATES.AWAITING_REGENERATE_INPUT);
    state.setChatInputLocked(false);
  }, []); // 의존성 제거

  // ============================================
  // [New] 응용 과제 (Challenge) 요청
  // ============================================

  const requestChallenge = useCallback(async () => {
    try {
      const state = useLunaStore.getState();
      const { currentLessonPlan, lastCodeSnapshot } = state;

      let originalProblem = null;
      console.log("[useLunaFlow] currentLessonPlan:", currentLessonPlan);

      if (currentLessonPlan?.steps) {
        console.log("[useLunaFlow] Steps:", currentLessonPlan.steps.map((s, i) => ({
          index: i,
          type: s.type,
          is_applied_challenge: s.is_applied_challenge,
          has_problem_json: !!s.problem_json,
          problem_json_keys: s.problem_json ? Object.keys(s.problem_json) : null
        })));

        const finalCodeStep = currentLessonPlan.steps.find(
          s => s.type === 'final_code' && !s.is_applied_challenge
        );
        console.log("[useLunaFlow] Found finalCodeStep:", finalCodeStep);

        if (finalCodeStep?.problem_json) {
          originalProblem = finalCodeStep.problem_json;
        } else if (finalCodeStep) {
          console.log("[useLunaFlow] No problem_json, using step itself as problem");
          originalProblem = {
            title: finalCodeStep.title || "응용 문제",
            description: finalCodeStep.description || finalCodeStep.content || "",
            code_template: finalCodeStep.code_template || "",
            language: finalCodeStep.language || currentLessonPlan.language || "html"
          };
        }
      }

      console.log("[useLunaFlow] Final originalProblem:", originalProblem);

      if (!originalProblem) {
        state.addChatMessage('ai', '현재 문제 정보를 찾을 수 없어요. 😢');
        return;
      }

      state.setLoading(true, '방금 푼 문제를 변형해서 응용 문제를 만들어볼게요! 잠시만 기다려주세요. 🤖');

      const { currentCurriculum, currentStepIndex } = useLunaStore.getState();
      let learnedKeywords = [];

      console.log("[useLunaFlow] Requesting Applied Challenge with:", originalProblem);
      const challengeData = await api.requestAppliedChallenge(originalProblem, lastCodeSnapshot || '', learnedKeywords);
      console.log("[useLunaFlow] Received Challenge Data:", challengeData);

      // [Refactor] await 이후 상태 업데이트
      const nextState = useLunaStore.getState();
      nextState.setProblem(challengeData);

      const plan = nextState.currentLessonPlan; // 최신 plan 조회
      if (plan && plan.steps) {
        const updatedPlan = JSON.parse(JSON.stringify(plan));

        const challengeStep = {
          ...updatedPlan.steps[0],
          ...challengeData,
          title: "🔥 응용 문제",
          type: "final_code",
          step_type: "final_code",
          is_applied_challenge: true,
          problem_json: {
            ...challengeData,
            title: "🔥 응용 문제",
            description: (challengeData.description || challengeData.challenge_description || "문제가 생성되었습니다.") +
              (Array.isArray(challengeData.requirements) && challengeData.requirements.length > 0
                ? `\n\n### 요구사항\n${challengeData.requirements.map(r => `- ${r}`).join('\n')}`
                : ''),
            requirements: challengeData.requirements
          },
        };

        const lastStep = updatedPlan.steps[updatedPlan.steps.length - 1];
        if (lastStep.is_applied_challenge) {
          updatedPlan.steps[updatedPlan.steps.length - 1] = challengeStep;
        } else {
          updatedPlan.steps.push(challengeStep);
        }

        nextState.setLessonPlan(updatedPlan);
        nextState.setLessonStepIndex(updatedPlan.steps.length - 1);

        nextState.setLessonCompleted(false);
        nextState.setAppState(APP_STATES.PROBLEM_SOLVING);

        nextState.addChatMessage('ai', '새로운 응용 문제가 준비되었어요! 한번 풀어볼까요? 💪');
      }

    } catch (error) {
      console.error('[Flow] Challenge 생성 실패:', error);
      const errorState = useLunaStore.getState();
      errorState.addChatMessage('ai', `응용 문제를 생성하는 중 오류가 발생했어요. 😢\n${error.message}`);
    } finally {
      useLunaStore.getState().setLoading(false);
    }
  }, []); // 의존성 제거

  // ============================================
  // 반환값
  // ============================================

  return {
    appState,
    currentCurriculum,
    currentStepIndex,
    currentLessonPlan,
    currentLessonStepIndex,
    chatHistory,
    intendedTopic,
    isRoadmapMode,
    lessonCompleted,
    chatInputLocked,
    problemJSON,

    handleChatSubmit,
    handleStepClick,
    startLesson,
    goToNextStep,
    handleInProblemChat,

    generateRoadmap,
    generateCurriculum,

    selectRoadmapLearning,
    selectCurriculumLearning,

    goToNextLessonStep: goToNextLessonStepWithSave,
    goToPrevLessonStep: goToPrevLessonStepWithSave,

    requestRegenerate,
    requestChallenge,
    saveCurrentState,

    isPositiveResponse,
    isCareerGoalRequest,
    escapeHtml,

    resetApp,
    startLesson,
  };
}

export default useLunaFlow;
